import SmallLogo from "@customer-gallery-public/logo_small.svg";
import Wave from "@customer-gallery-public/bottomWave.png";
import FloatingBalls from "@customer-gallery-public/floatingBalls.png";
import { customerRoutePush, CustomerTypedRoute } from "@customer-gallery/definitions/routeDefinitions";
import { requestLoginTokenCodeEmail } from "@customer-gallery/redux/actionCreators/auth";
import { useNovelCustomerDispatch, useNovelCustomerSelector } from "@customer-gallery/redux/reduxHooks";
import { Box } from "@novel/shared/components/Box";
import { NovelModal } from "@novel/shared/components/NovelModal";
import { useIsMobile } from "@novel/shared/hooks/useIsMobile";
import { Button, Form, FormLayout } from "@shopify/polaris";
import cx from "classnames";
import Image from "next/legacy/image";
import React, { useCallback, useEffect, useState } from "react";
import { typedFrontendGalleryApi } from "@customer-gallery/utils/typedFrontendGalleryApi";
import Head from "next/head";
import { SixDigitCodeComponent } from "@novel/shared/components/SixDigitCodeComponent";
import { useAsyncCallback } from "@novel/shared/utils/useAsyncCallback";
import { SkeletonLoadingBackground } from "@customer-gallery/components/SkeletonLoadingBackground";
import { dismissToast, loadingToast } from "@novel/shared/utils/toastUtils";
type LoginPageStates = "login-request" | "login-auth" | "login-code-provided-via-url";
const CODE_ERROR = "You have input the wrong code, please try again.";
const CODE_RESEND = "A new code has been sent to your email.";
const LoginPage: CustomerTypedRoute<"/login"> = ({
  props
}) => {
  const providedEmail = (props.email as string);
  const providedCode = (props.code as string);
  const redirectAppUrl = (decodeURIComponent(props.redirectAppUrl ? props.redirectAppUrl : "") as string);
  const userIsLoading = useNovelCustomerSelector(state => state.auth.isLoadingUser);
  const user = useNovelCustomerSelector(state => state.auth.user);
  const isMobile = useIsMobile();
  const [email, setEmail] = useState<string>(providedEmail || "");
  const loginEmailStatus = useNovelCustomerSelector(state => state.auth.loginEmailStatus);
  const dispatch = useNovelCustomerDispatch();
  const onEmailChange = (e: any) => {
    setEmail(e.target.value && e.target.value.trim());
  };
  const onSubmit = useAsyncCallback(async () => {
    const properEmail = typeof email === "undefined" ? providedEmail : email;
    await dispatch(requestLoginTokenCodeEmail({
      email: properEmail
    }));
    customerRoutePush("/login", {}, {
      email: properEmail
    });
  }, [dispatch, email, providedEmail]);
  const [loginModalState, setLoginModalState] = useState(("login-code-provided-via-url" as LoginPageStates));
  useEffect(() => {
    if (!userIsLoading && !user) {
      if (providedEmail) {
        if (providedCode && providedCode.length === 6) {
          setLoginModalState("login-code-provided-via-url");
        } else if (providedEmail.length > 0) {
          setLoginModalState("login-auth");
        }
      } else {
        setLoginModalState("login-request");
      }
    }
  }, [providedEmail, providedCode, userIsLoading, user]);
  const redirectToLoginSuccess = useCallback(() => {
    setLoginModalState("login-auth");
  }, []);
  const [codeMessage, setCodeMessage] = useState("");
  const [modalWiggle, setModalWiggle] = useState(false);
  const [sixDigitCode, sixDigitCodeOnChange] = useState("");
  const checkCodeAndLogin = useAsyncCallback(async (code: string, errorHandle: () => void) => {
    const {
      onSuccessToast,
      onErrorToast
    } = loadingToast("Verifying auth code...");
    await typedFrontendGalleryApi.postReq("/api/customer/auth/passwordless/login/code", {
      reqBody: {
        code,
        email: providedEmail || email
      }
    }).then(async res => {
      if (res.type === "error") {
        throw res.error;
      }

      // if they're coming from an auth URL then showing the toast looks odd
      if (providedCode) {
        onSuccessToast("Authentication Verified!");
      }
      return await typedFrontendGalleryApi.getReq("/api/customer/auth/gallery/session");
    }).then(async sessionLoadRes => {
      if (sessionLoadRes.type === "error") {
        throw sessionLoadRes.error;
      }
      await dispatch({
        type: "LOADED_USER",
        payload: {
          user: sessionLoadRes.body.user || null
        }
      });
      setTimeout(() => {
        customerRoutePush((redirectAppUrl as any) || "/");
      }, 0);
      setTimeout(() => {
        dismissToast();
      }, 1000);
    }).catch(error => {
      onErrorToast(error.message);
      errorHandle();
    });
  }, [dispatch, sixDigitCode, email, providedEmail]);
  useEffect(() => {
    if (!userIsLoading && !user && providedEmail && providedCode && providedCode.length === 6) {
      checkCodeAndLogin(providedCode, () => {
        customerRoutePush("/login", {}, {
          email: providedEmail
        });
        setLoginModalState("login-auth");
      });
    }
  }, [providedCode, providedEmail, checkCodeAndLogin, user, userIsLoading]);
  useEffect(() => {
    if ((sixDigitCode || "").trim().length === 6) {
      checkCodeAndLogin(sixDigitCode, () => {
        setCodeMessage(CODE_ERROR);
        setModalWiggle(true);
        sixDigitCodeOnChange("");
        document!.getElementById("lastDigit")!.blur();
        document!.getElementById("firstDigit")!.focus();
        setTimeout(() => setModalWiggle(false), 1000);
      });
    }
  }, [sixDigitCode, checkCodeAndLogin]);
  useEffect(() => {
    if (loginEmailStatus === "success") {
      dispatch({
        type: "CLEAR_LOGIN_TOKEN_EMAIL_STATE",
        payload: undefined
      });
      redirectToLoginSuccess();
    }
  }, [loginEmailStatus, redirectToLoginSuccess, dispatch]);
  return <React.Fragment>
            <Head>
                <title>Login | Novel</title>
            </Head>
            <div className="frame-wrapper flex">
                <SkeletonLoadingBackground>
                    {loginModalState !== "login-code-provided-via-url" && <NovelModal className="gallery-login-modal" unclosable open fullScreenOnMobile>
                            <Box column rowGap={5.5} style={{
            padding: `${isMobile ? "20px" : "0px"}`
          }}>
                                <Box className="small-logo-container">
                                    <Image src={SmallLogo} alt="Logo" layout="fill" objectFit="contain" priority />
                                </Box>
                                {loginModalState === "login-request" && <React.Fragment>
                                        <Box column rowGap={1}>
                                            <Box className="hello-text">Hello!</Box>
                                            <Box className="hello-subtext">to your gallery</Box>
                                        </Box>
                                        <Box column>
                                            <Form onSubmit={onSubmit}>
                                                <FormLayout>
                                                    <input autoFocus className="auth-email-input" placeholder="Enter Your Email" type="email" id="email" name="email" autoComplete="email" onChange={onEmailChange} />
                                                    <div className="form-submit-area">
                                                        <Button submit disabled={loginEmailStatus === "requesting"}>
                                                            Continue
                                                        </Button>
                                                        <div className="account-extra">
                                                            <h3 className="already-account">
                                                                Don't have an account?
                                                            </h3>
                                                            <button onClick={() => customerRoutePush("/sign-up")} type="button" className="edit-text-light">
                                                                <Box style={{
                            flex: 1,
                            justifyContent: "center",
                            alignItems: "center"
                          }} columnGap={1}>
                                                                    Sign up
                                                                </Box>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </FormLayout>
                                            </Form>
                                        </Box>
                                    </React.Fragment>}
                                {loginModalState === "login-auth" && <Box column rowGap={5.5}>
                                        <Box column rowGap={1}>
                                            <Box className="auth-text">
                                                Enter Your Security Code
                                            </Box>
                                            <Box className="auth-subtext">
                                                Please enter the six digit code found in your email{" "}
                                                <span style={{
                    display: "contents"
                  }}>
                                                    {" "}
                                                    {providedEmail}
                                                </span>
                                            </Box>
                                        </Box>
                                        <SixDigitCodeComponent className={cx({
                modalWiggle
              })} value={sixDigitCode} onChange={sixDigitCodeOnChange} />
                                        <Box style={{
                fontSize: "12px",
                marginTop: "-2rem"
              }} column rowGap={1}>
                                            {!!codeMessage.length && <Box className="resend-code" style={{
                  cursor: "auto",
                  color: codeMessage === CODE_ERROR ? "red" : "black"
                }}>
                                                    <p>{codeMessage}</p>
                                                </Box>}
                                            <p style={{
                  color: "#ccc"
                }}>
                                                Didn’t receive your code?
                                                <span className="resend-code" onClick={async () => {
                    await onSubmit();
                    setCodeMessage(CODE_RESEND);
                  }}>
                                                    {" "}
                                                    Get a new one
                                                </span>
                                            </p>
                                        </Box>
                                    </Box>}
                            </Box>
                            <img className="wave-image" src={Wave.src} />
                            <img className="balls-image" src={FloatingBalls.src} />
                        </NovelModal>}
                </SkeletonLoadingBackground>
            </div>
            <style global jsx>{`
                .rs-modal-body {
                    overflow: hidden !important;
                }
                .form-submit-area {
                    display: flex;
                    flex-direction: column;
                }
                .already-account {
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 16px;
                    display: inline-block;
                }

                .edit-text-light {
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 16px;
                    text-decoration-line: underline;
                    cursor: pointer;
                    margin-left: 5px;
                }
                .account-extra {
                    margin-top: 10px;
                }
                .rs-modal-content {
                    background: #fff;
                    height: 556px;
                    max-width: 587px;
                    border-radius: 10px !important;
                    padding-left: 20px;
                    padding-top: 20px;
                    overflow: hidden;
                    position: relative;
                }
                .small-logo-container {
                    height: 60px;
                    width: 60px;
                }
                .hello-text {
                    font-size: 40px;
                    color: black;
                    font-weight: 600;
                    font-family: "Poppins", sans-serif;
                }
                .hello-subtext {
                    font-size: 25px;
                    color: #ccc;
                    font-weight: 600;
                    font-family: "Poppins", sans-serif;
                }
                .auth-text {
                    font-size: 28px;
                    color: black;
                    font-weight: 600;
                    font-family: "Poppins", sans-serif;
                }
                .auth-subtext {
                    font-size: 13px;
                    font-family: "Poppins", sans-serif;
                }
                .Polaris-Button {
                    border-radius: 100px;
                    background: black;
                    height: 54px;
                    width: 167px;
                    border: none;
                    overflow: hidden;
                    transition: all 0.4s ease-in-out;
                }
                .Polaris-Button:active,
                .Polaris-Button:hover {
                    background: #000;
                    opacity: 0.7;
                    box-shadow:
                        0px 11px 15px rgba(0, 0, 0, 0.1),
                        0px 9px 46px rgba(0, 0, 0, 0.06),
                        0px 24px 38px rgba(0, 0, 0, 0.04) !important;
                }
                .Polaris-Button__Text {
                    font-family: "Poppins", sans-serif;
                    font-size: 14px;
                    color: white;
                    font-weight: 600;
                }
                .login-auth-auth-state {
                    margin: 4rem 0;
                    text-align: center;
                }
                .login-auth-auth-state > button {
                    width: 50%;
                }

                .code-message {
                    margin: 1rem 0;
                }
                .resend-code {
                    font-weight: bold;
                    display: contents;
                    color: black;
                    cursor: pointer;
                }
                .Polaris-SkeletonDisplayText__DisplayText {
                    max-width: calc(100% - 2rem);
                    width: 100%;
                    margin-top: 1.5rem;
                }
                .modalWiggle {
                    -webkit-animation: kf_shake 0.4s 1 linear;
                    -moz-animation: kf_shake 0.4s 1 linear;
                    -o-animation: kf_shake 0.4s 1 linear;
                }
                @-webkit-keyframes kf_shake {
                    0% {
                        -webkit-transform: translate(30px);
                    }
                    20% {
                        -webkit-transform: translate(-30px);
                    }
                    40% {
                        -webkit-transform: translate(15px);
                    }
                    60% {
                        -webkit-transform: translate(-15px);
                    }
                    80% {
                        -webkit-transform: translate(8px);
                    }
                    100% {
                        -webkit-transform: translate(0px);
                    }
                }
                @-moz-keyframes kf_shake {
                    0% {
                        -moz-transform: translate(30px);
                    }
                    20% {
                        -moz-transform: translate(-30px);
                    }
                    40% {
                        -moz-transform: translate(15px);
                    }
                    60% {
                        -moz-transform: translate(-15px);
                    }
                    80% {
                        -moz-transform: translate(8px);
                    }
                    100% {
                        -moz-transform: translate(0px);
                    }
                }
                @-o-keyframes kf_shake {
                    0% {
                        -o-transform: translate(30px);
                    }
                    20% {
                        -o-transform: translate(-30px);
                    }
                    40% {
                        -o-transform: translate(15px);
                    }
                    60% {
                        -o-transform: translate(-15px);
                    }
                    80% {
                        -o-transform: translate(8px);
                    }
                    100% {
                        -o-origin-transform: translate(0px);
                    }
                }

                .balls-image {
                    position: absolute;
                    right: 10%;
                    bottom: 1.5%;
                    max-width: 100%;
                    max-height: 100%;
                }

                @media (min-width: 768px) {
                    .auth-code-container {
                        width: 80%;
                    }
                    .balls-image {
                        max-height: 135px;
                    }
                    .rs-modal-content {
                        padding-left: 77px;
                        padding-top: 73px;
                        padding-right: 77px;
                    }
                    .form-submit-area {
                        display: flex;
                        align-items: center;
                        flex-direction: row;
                    }
                    .account-extra {
                        display: inline-block;
                        margin-left: 20px;
                    }
                }
                @media (max-height: 768px) {
                    .balls-image {
                        max-width: 40%;
                    }
                }
            `}</style>
        </React.Fragment>;
};
LoginPage.isPublicRoute = true;
LoginPage.isLoggedOutRoute = true;
export default LoginPage;